import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { getRegionByPostalCode } from '@dispatch/shared/services';

interface UseZipCodeToRegionProps {
  onStateChange: (state: string | null) => void;
  onCountyChange?: (county: string | null) => void;
  onErrorChange: (error: string | null) => void;
  onLoadingChange: (loading: boolean) => void;
  tenantId: string;
}

interface UseZipCodeToRegionReturn {
  handleZipCodeChange: (zipCode: string) => void;
  isValidZipFormat: (zipCode: string) => boolean;
  formatZipCode: (value: string) => string;
  hasInvalidFormat: (zipCode: string) => boolean;
  resetLastValidZip: () => void;
}

export const useZipCodeToRegion = ({
  onStateChange,
  onCountyChange,
  onErrorChange,
  onLoadingChange,
  tenantId,
}: UseZipCodeToRegionProps): UseZipCodeToRegionReturn => {
  const [lastValidZip, setLastValidZip] = useState<string>('');

  const isValidZipFormat = useCallback((zipCode: string): boolean => {
    const cleanZip = zipCode.replace(/[^0-9]/g, '');
    return cleanZip.length === 5 || cleanZip.length === 9;
  }, []);

  const hasInvalidFormat = useCallback((zipCode: string): boolean => {
    if (!zipCode || zipCode.length === 0) return false;

    const validPattern = /^\d{1,5}(-\d{0,4})?$/;
    if (!validPattern.test(zipCode)) return true;

    const cleanZip = zipCode.replace(/[^0-9]/g, '');
    if (cleanZip.length > 9) return true;

    if (zipCode.includes('-')) {
      const parts = zipCode.split('-');
      if (parts.length !== 2) return true;
      if (parts[0].length !== 5) return true;
      if (parts[1].length > 0 && parts[1].length !== 4) return true;
    }

    return false;
  }, []);

  const formatZipCode = useCallback((value: string): string => {
    const cleanValue = value.replace(/[^0-9]/g, '');

    if (cleanValue.length <= 5) {
      return cleanValue;
    }

    if (cleanValue.length <= 9) {
      return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5)}`;
    }

    return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5, 9)}`;
  }, []);

  const fetchRegionByZipCode = useCallback(
    async (zipCode: string) => {
      if (!isValidZipFormat(zipCode)) {
        return;
      }

      const cleanZip = zipCode.replace(/[^0-9]/g, '');

      if (cleanZip === lastValidZip) {
        return;
      }

      setLastValidZip(cleanZip);
      onLoadingChange(true);
      onErrorChange(null);

      try {
        const result = await getRegionByPostalCode(zipCode, tenantId);

        if (result.status === 200 && result.data) {
          onStateChange(result.data.stateCode);
          onCountyChange?.(result.data.county ?? null);
          onErrorChange(null);
        } else if (result.status === 404) {
          onStateChange(null);
          onCountyChange?.(null);
          const errorMessage =
            result.error?.detail || 'No region found for postal code.';
          onErrorChange(`${errorMessage} Please enter a valid zip.`);
        } else if (result.status === 400) {
          onStateChange(null);
          onCountyChange?.(null);
          onErrorChange(
            'Invalid code format, please enter 5 or 9 digits only.'
          );
        } else {
          onStateChange(null);
          onCountyChange?.(null);
          onErrorChange(
            'We encountered an internal error with the ZIP code resolution service. Please try again later.'
          );
        }
      } catch (error) {
        onStateChange(null);
        onCountyChange?.(null);
        onErrorChange(
          'We encountered an internal error with the ZIP code resolution service. Please try again later.'
        );
      } finally {
        onLoadingChange(false);
      }
    },
    [
      isValidZipFormat,
      lastValidZip,
      onStateChange,
      onCountyChange,
      onErrorChange,
      onLoadingChange,
    ]
  );

  const fetchRef = useRef(fetchRegionByZipCode);
  fetchRef.current = fetchRegionByZipCode;

  const stableDebouncedFetch = useMemo(() => {
    return debounce((zipCode: string) => {
      fetchRef.current(zipCode);
    }, 500);
  }, []);

  const debouncedValidation = useMemo(() => {
    return debounce((zipCode: string) => {
      const cleanZip = zipCode.replace(/[^0-9]/g, '');
      if (cleanZip.length > 0 && cleanZip.length < 5) {
        onErrorChange(
          'Invalid code format, please re-check it - should be 5 or 9 digits'
        );
      }
    }, 1000);
  }, [onErrorChange]);

  const resetLastValidZip = useCallback(() => {
    setLastValidZip('');
  }, []);

  const handleZipCodeChange = useCallback(
    (zipCode: string) => {
      const formattedZip = formatZipCode(zipCode);

      if (hasInvalidFormat(formattedZip)) {
        onErrorChange(
          'Invalid code format, please re-check it - should be 5 or 9 digits'
        );
        stableDebouncedFetch.cancel();
        debouncedValidation.cancel();
      } else if (isValidZipFormat(formattedZip)) {
        onErrorChange(null);
        stableDebouncedFetch(formattedZip);
        debouncedValidation.cancel();
      } else {
        onErrorChange(null);
        stableDebouncedFetch.cancel();
        debouncedValidation(formattedZip);
      }
    },
    [
      formatZipCode,
      hasInvalidFormat,
      isValidZipFormat,
      stableDebouncedFetch,
      debouncedValidation,
      onErrorChange,
    ]
  );

  useEffect(() => {
    return () => {
      stableDebouncedFetch.cancel();
      debouncedValidation.cancel();
    };
  }, [stableDebouncedFetch, debouncedValidation]);

  return {
    handleZipCodeChange,
    isValidZipFormat,
    formatZipCode,
    hasInvalidFormat,
    resetLastValidZip,
  };
};
